import { ExtrudeFeatureDefinitionModel } from '../../../definition/extrude/ExtrudeFeatureDefinition';
import type { FeatureEditSession } from '../../../edit/FeatureEditSession';
import { BossExtrudeFeaturePrototype } from '../../../prototype/extrude/BossExtrudeFeaturePrototype';
import { CutExtrudeFeaturePrototype } from '../../../prototype/extrude/CutExtrudeFeaturePrototype';
import { resolveSketchProfileSourceSketchId } from '../../../../utils/feature/resolveSketchProfileSourceSketchId';
import { ExtrudePreviewExecution } from './ExtrudePreviewExecution';
import { FeaturePreviewExecutionHandler, type FeaturePreviewExecutionInput, type FeaturePreviewExecutionResult } from '../FeaturePreviewExecutionHandler';

/**
 * 拉伸特征预览执行处理器。
 */
export class ExtrudePreviewExecutionHandler extends FeaturePreviewExecutionHandler {
    /** 拉伸预览执行器。 */
    private readonly execution: ExtrudePreviewExecution;

    /**
     * @param execution 拉伸预览执行器。
     */
    public constructor(execution: ExtrudePreviewExecution) {
        super();
        this.execution = execution;
    }

    /**
     * @inheritdoc
     */
    public supports(input: FeaturePreviewExecutionInput): boolean {
        return (
            (input.featureKind === 'boss-extrude' || input.featureKind === 'cut-extrude')
            && input.definition instanceof ExtrudeFeatureDefinitionModel
        );
    }

    /**
     * 执行拉伸特征的预览求解。
     *
     * 设计说明：
     * 1. 此方法根据输入参数，判断并构建对应的拉伸（基体或切除）特征原型（Prototype）对象，用于描述即将预览的特征实例及其参数。
     * 2. 根据输入是否包含编辑会话（EditSession），决定调用“编辑态预览”还是“创建态预览”的执行方法：
     *    - 编辑态（input.session 存在）：调用 executeEditPreview，实现对特征编辑中的参数变化进行实时预览。
     *    - 创建态：调用 executeCreatePreview，实现对新特征创建参数的实时预览。
     * 3. 通过调用 this.execution.executeEditPreview 或 this.execution.executeCreatePreview，
     *    在这两个方法内部进一步调用了 ExtrudePreviewExecution 中的 workerManager.generateExtrudePreview 或
     *    workerManager.generateExtrudePreviewInSandbox 方法，这些方法最终向 Puppy CAD 内核 Worker 发起几何求解请求，
     *    获取 Mesh 数据。具体步骤如下：
     *    - 创建态：this.execution.executeCreatePreview(input.definition) ->
     *      workerManager.generateExtrudePreview，传递参数到 Worker 求解 Mesh。
     *    - 编辑态：this.execution.executeEditPreview(input.session, input.definition) ->
     *      workerManager.generateExtrudePreviewInSandbox，带上历史指令和参数到 Worker 求解 Mesh。

     * 4. 返回预览结果对象，包含 meshData（预览网格）、prototype（预览特征原型）、draftDefinition（参数草稿定义）、presentation（暂无，固定为 null）。
     *
     * 为什么这样做：
     * - 必须100%模拟 SolidWorks 行为：编辑或创建拉伸时随时获得符合参数的三维预览效果。
     * - 需要将“草图-特征-预览”建模为统一的链路，便于后续扩展和集成。
     * - 所有输入输出皆高度结构化，便于实现撤销重做、参数回溯、与 UI 交互的自动同步。
     * 
     * @param input 特征预览执行输入参数，包含特征类型、参数定义、引用绑定、编辑会话等。
     * @returns 特征预览执行结果，包含预览网格数据与特征原型。
     * @throws 当预览输入中的定义不是拉伸特征定义时。
     */
    public async execute(input: FeaturePreviewExecutionInput): Promise<FeaturePreviewExecutionResult> {
        // 检查 definition 类型，必须为 ExtrudeFeatureDefinitionModel，否则抛出异常
        if (!(input.definition instanceof ExtrudeFeatureDefinitionModel)) {
            throw new Error('拉伸预览处理器收到的 definition 类型不正确');
        }
        // 解析出用于参数化特征的草图ID（支持引用/绑定的场景）
        const sourceSketchId = resolveSketchProfileSourceSketchId({
            definitionSourceSketchId: input.definition.getSourceSketchId(),
            bindings: input.bindings,
        });
        // 构建对应类型（基体/切除）的拉伸特征原型
        const prototype = input.featureKind === 'cut-extrude'
            ? new CutExtrudeFeaturePrototype({
                featureId: input.featureId ?? `preview:${Date.now()}`,
                definition: input.definition,
                bindings: input.bindings,
                sourceSketchId,
            })
            : new BossExtrudeFeaturePrototype({
                featureId: input.featureId ?? `preview:${Date.now()}`,
                definition: input.definition,
                bindings: input.bindings,
                sourceSketchId,
            });
        // 此处会调用 ExtrudePreviewExecution 内部的 executeEditPreview 或 executeCreatePreview，
        // 这两个方法都会进一步调用 Worker（Puppy CAD 内核 后端），进行三维几何求解，并解析生成 meshData（三维网格数据）。
        // 也就是说，调用到这里时，已经完成 Puppy CAD 内核 Worker 侧的实体建模和 mesh 数据的解析。
        const previewResult = input.session
            ? await this.execution.executeEditPreview(
                input.session as FeatureEditSession<ExtrudeFeatureDefinitionModel>,
                input.definition,
            )
            : await this.execution.executeCreatePreview(input.definition);

        // 返回标准结构的预览结果
        return {
            meshData: previewResult.meshData,
            presentation: null,
            prototype,
            draftDefinition: input.definition,
        };
    }
}
